import { ChoiceGroup, DefaultButton, DialogFooter, IChoiceGroupOption, Label, MessageBar, MessageBarType, PrimaryButton, ProgressIndicator, Stack, TextField } from "@fluentui/react"
import { useCallback, useEffect, useState } from "react"
import { useApiCall } from "../hooks/apiHooks"
import { getRaterTierFormattedFromCoverageTypeValue, safeFormatDateValue, safeParseDateTime } from "../lib/helper"
import { API_METHODS, makeApiCall } from "../services/apiService"
import { downloadBase64Content } from "../services/helperService"
import { IAccount } from "../types/IAccount"
import { PlanSelector } from "./planSelector"

interface RaterDialogProps {
	group: IAccount
	onDismiss: () => void
	isCRM: boolean
}

const ratingMethodOptions: IChoiceGroupOption[] = [
	{
		key: "Composite",
		text: "Tier Rated"
	},
	{
		key: "Age Rated",
		text: "Age Rated"
	}
]

const tierLevelOptions: IChoiceGroupOption[] = [
	{
		key: "Five Tier",
		text: "Five Tier"
	},
	{
		key: "Four Tier",
		text: "Four Tier"
	}
]

type RaterOutput = {
	data: {
		fileContent: string
	}
	error: string | null
	fileName: string
}

type TypeOfQuote = "Initial Quote"

type RatingMethod = "Composite" | "Age Rated"

type CensusType = "Five Tier" | "Four Tier"

interface IPlan {
	PlanSelection: string
	PlanType: string
}

interface Plans {
	Plan1: IPlan
	Plan2: IPlan
	Plan3: IPlan
	Plan4: IPlan
	Plan5: IPlan
}

interface IGroupData {
	groupId: string
	isProspect: boolean
	groupName: string
	groupCode: string
	agencyName: string
	brokerName: string
	effectiveDate: string
	quoteInitialOrRenewal: TypeOfQuote
	zipCode: string
	industryType: "All Other Approved Industries"
	quoteType: "Preliminary"
	ratingMethod: RatingMethod
	censusType: CensusType
	plans: Plans
}

type CensusTier = "EE" | "ES" | "EC" | "E1" | "EF"

interface ICensusRaterInputRow {
	name: string
	dob: string
	gender: "M" | "F"
	tier: CensusTier
	zipCode?: string
}

interface IQuotePayload {
	groupData: IGroupData
	censusData: ICensusRaterInputRow[]
}

interface IRaterPayload extends IQuotePayload {
	vendor: string
}

interface IDataverseEmployee {
	"cw_birthdate@OData.Community.Display.V1.FormattedValue": string
	cw_birthdate: string
	cw_name: string
	cw_zipcode: string
	"_cw_plan_value@OData.Community.Display.V1.FormattedValue": string
	_cw_plan_value: string
	"cw_healthcarecoveragetype@OData.Community.Display.V1.FormattedValue": string
	cw_healthcarecoveragetype: number
	cw_employeeid: string
	cw_gender: number
	"cw_gender@OData.Community.Display.V1.FormattedValue": string
}

const zipCodePrefixes: string[] = ["201", ...Array.from({ length: 27 }, (_, i) => (220 + i).toString())]

// export function RaterDialog(props: RaterDialogProps) {
// 	const { group, onDismiss } = props
// 	return (
// 		<Dialog
// 			hidden={false}
// 			minWidth={500}
// 			onDismiss={onDismiss}
// 			dialogContentProps={{
// 				type: DialogType.normal,
// 				title: `Generate a Quote: ${group.name}`,
// 				closeButtonAriaLabel: "Close"
// 			}}
// 			modalProps={{
// 				isBlocking: false
// 			}}>
// 			<RaterForm onDismiss={onDismiss} group={group} />
// 		</Dialog>
// 	)
// }

export function RaterForm(props: RaterDialogProps) {
	const { onDismiss, group } = props

	const [groupName, setGroupName] = useState(group.name)
	const typeOfQuote: TypeOfQuote = "Initial Quote"
	const [ratingMethod, setRatingMethod] = useState<RatingMethod>()
	const [tierLevel, setTierLevel] = useState<CensusType>()
	const effectiveDate = safeFormatDateValue(group.cw_coveragestartdate, "M/1/yyyy")

	const [employees, setEmployees] = useState<IDataverseEmployee[]>()
	const [generatingQuoteMessage, setGeneratingQuoteMessage] = useState<string>("Please be patient as this can take up to a minute...")
	const [quotePayload, setQuotePayload] = useState<IQuotePayload>()
	const [raterPayload, setRaterPayload] = useState<IRaterPayload>()
	const [loadingRater, setLoadingRater] = useState<boolean>(false)
	const [isPayloadCensusValid, setIsPayloadCensusValid] = useState<boolean>(true)
	const [isZipCodeDataValid, setIsZipCodeDataValid] = useState<boolean>(false)
	const [isEffectiveDateValid, setIsEffectiveDateValid] = useState<boolean>(true)
	const [showPlanSelector, setShowPlanSelector] = useState<boolean>(false)
	const [selectedPlans, setSlectedPlans] = useState<string[]>([])

	useEffect(() => {
		if (employees) {
			let allZipCodesValid = true
			for (let i = 0; i < employees.length; i++) {
				const employee = employees[i]
				if (employee.cw_zipcode && !zipCodePrefixes.some(prefix => employee.cw_zipcode.startsWith(prefix))) {
					allZipCodesValid = false
					break
				}
			}
			setIsZipCodeDataValid(allZipCodesValid)
		}
	}, [employees])

	useEffect(() => {
		if (!isSubmitDisabled()) {
			setQuotePayload(getQuotePayload())
		}
		if (!isRunRaterDisabled()) {
			const payloadData = getQuotePayload()
			const raterPayload: IRaterPayload = { ...payloadData, vendor: "BD" }
			setRaterPayload(raterPayload)
		}
		if ((group.cw_coveragestartdate && !(effectiveDate !== "Invalid Date")) || safeParseDateTime(effectiveDate) < safeParseDateTime(group.cw_coveragestartdate)) {
			setIsEffectiveDateValid(false)
		}
	}, [groupName, typeOfQuote, ratingMethod, tierLevel, effectiveDate, selectedPlans, employees, group.cw_coveragestartdate])

	const getQuotePayload = (): IQuotePayload => {
		if (employees && selectedPlans) {
			const plans: any = {}
			selectedPlans?.forEach((p, i) => {
				plans[`Plan${i + 1}`] = {
					PlanSelection: p,
					PlanType: "Value"
				}
			})
			const groupZipCode = group.address1_postalcode ? group.address1_postalcode.substring(0, 3) : "201"
			const groupData: IGroupData = {
				groupId: group.accountid,
				isProspect: group.cw_accounttype.includes("Prospect"),
				groupName: groupName,
				groupCode: group.cw_groupcode || "",
				agencyName: group.cw_Brokerage?.cw_name || "",
				brokerName: group.cw_PreferredBroker?.fullname || "",
				effectiveDate: effectiveDate,
				quoteInitialOrRenewal: typeOfQuote,
				zipCode: groupZipCode,
				industryType: "All Other Approved Industries",
				quoteType: "Preliminary",
				ratingMethod: ratingMethod,
				censusType: tierLevel,
				plans: plans
			}
			const censusData: ICensusRaterInputRow[] = employees
				.map(e => {
					const employeeTier = getRaterTierFormattedFromCoverageTypeValue(e.cw_healthcarecoveragetype)
					let zipCode = e.cw_zipcode || group.address1_postalcode
					if (zipCode) {
						if (e.cw_zipcode && !zipCodePrefixes.some(prefix => e.cw_zipcode.startsWith(prefix))) {
							zipCode = group.address1_postalcode // If the zip code is invalid, use the group zip code
						}
					}
					const censusRetVal: ICensusRaterInputRow = {
						name: e.cw_name,
						dob: e["cw_birthdate@OData.Community.Display.V1.FormattedValue"],
						gender: e.cw_gender === 121640000 ? "M" : "F",
						tier: employeeTier as CensusTier,
						zipCode: zipCode
					}
					return censusRetVal
				})
				.filter(e => e)
			if (censusData.length < 5) {
				setIsPayloadCensusValid(false)
			} else {
				setIsPayloadCensusValid(true)
				const retVal: IQuotePayload = {
					groupData: groupData,
					censusData: censusData
				}
				return retVal
			}
		}

		return undefined
	}

	const {
		data: quote,
		loading: loadingQuote,
		error: errorGettingQuote,
		fetchData: generateQuote,
		setData: setQuote,
		abort: abortQuote
	} = useApiCall<RaterOutput>(`/getBrokerRatesWorkbook${props.isCRM ? "CRM" : ""}`, { handlerOnly: true, body: JSON.stringify(quotePayload), method: "POST" })

	const getExistingHouseholdMembers = useCallback(async () => {
		const body = {
			query: `/cw_employees?$filter=_cw_employer_value eq '${group.accountid}'&$select=cw_employeeid,cw_birthdate,cw_name,cw_zipcode,_cw_plan_value,cw_healthcarecoveragetype,cw_gender`
		}
		let customApiBaseUrl = ""
		let customAPIPath = ""
		let apiPath = "/makeCRMGetRequest"
		let method: API_METHODS = "POST"
		if (props.isCRM) {
			customApiBaseUrl = `https://${window.top.location.hostname}`
			customAPIPath = "api/data/v9.2.24042.218"
			apiPath = body.query.substring(1)
			method = "GET"
		}

		const response = await makeApiCall(
			apiPath,
			method,
			props.isCRM ? undefined : JSON.stringify(body),
			customAPIPath,
			{
				Prefer: 'return=representation,odata.include-annotations="*"'
			},
			undefined,
			undefined,
			undefined,
			customApiBaseUrl
		)
		const employees: IDataverseEmployee[] = response.value.filter((e: IDataverseEmployee) => e.cw_healthcarecoveragetype !== 121640001)
		setEmployees(employees)
	}, [group])

	useEffect(() => {
		getExistingHouseholdMembers()
	}, [group, getExistingHouseholdMembers])

	const isSubmitDisabled = () => {
		return (
			!groupName ||
			loadingQuote ||
			!employees ||
			employees.length < 5 ||
			!ratingMethod ||
			!typeOfQuote ||
			!tierLevel ||
			!effectiveDate ||
			!selectedPlans ||
			selectedPlans.length === 0 ||
			!isEffectiveDateValid
		)
	}

	const isRunRaterDisabled = () => {
		return !groupName || loadingQuote || !employees || employees.length < 5 || !ratingMethod || !typeOfQuote || !tierLevel || !effectiveDate || !selectedPlans || !isEffectiveDateValid
	}

	const downloadQuote = () => {
		if (quote) {
			downloadBase64Content(quote.data.fileContent, quote.fileName, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
		}
	}

	return (
		<div style={{ maxWidth: 740 }}>
			{employees && employees.length > 4 && (
				<Stack tokens={{ childrenGap: 8 }} style={{ maxHeight: props.isCRM ? "auto" : "calc(100vh - 296px)", overflowY: "auto" }}>
					{!isEffectiveDateValid && (
						<MessageBar messageBarType={MessageBarType.warning} isMultiline styles={{ root: { minHeight: "auto" } }}>
							The effective date is not valid or is in the past
						</MessageBar>
					)}
					<MessageBar messageBarType={MessageBarType.warning} styles={{ root: { minHeight: "auto" } }}>
						**Use this to generate the field rate quote for the group. This is not a final quote and should not be used for enrollment purposes.**
					</MessageBar>
					{!props.isCRM && (
						<MessageBar messageBarType={MessageBarType.info} isMultiline styles={{ root: { minHeight: "auto" } }}>
							Note: <br />
							All rates generated using this online Quote Generator tool are field rate quotes for quoting purposes and are non-binding. <br />
							If you would like to have a Field Rate quote underwritten, please reach out to ACMG via email and include the Field Rate quote output received via the Quote Generator.
						</MessageBar>
					)}
					<Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
						<Label>Number of Employees:</Label>
						<span>{employees.length}</span>
					</Stack>
					<TextField
						disabled={loadingQuote}
						label="Group Name"
						value={groupName}
						onChange={(_ev, newValue) => setGroupName(newValue || "")}
						required
						styles={{
							root: {
								maxWidth: 300
							}
						}}
					/>
					<Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
						<Label>Effective Date:</Label>
						<span>{safeFormatDateValue(group.cw_coveragestartdate, "MM/1/yyyy")}</span>
					</Stack>
					<ChoiceGroup
						disabled={loadingQuote}
						label="Rating Method"
						options={ratingMethodOptions}
						onChange={(_ev, option) => setRatingMethod(option.key.toString() as RatingMethod)}
						selectedKey={ratingMethod}
						required
					/>
					<ChoiceGroup
						label="Tier Level"
						disabled={loadingQuote}
						options={tierLevelOptions}
						onChange={(_ev, option) => setTierLevel(option.key.toString() as CensusType)}
						selectedKey={tierLevel}
						required
					/>
					<Stack tokens={{ childrenGap: 4 }} verticalAlign="center">
						<Label>Plans:</Label>
						{selectedPlans && selectedPlans.length > 0 ? (
							<PlanSelector
								onDismiss={() => {
									/*void*/
								}}
								onSave={_plans => {
									/*void*/
								}}
								defaultSelectedPlans={selectedPlans || []}
								displayOnly
								onlyShowPlans={selectedPlans}
								isCRM={props.isCRM}
							/>
						) : (
							"None"
						)}
					</Stack>
					{!loadingQuote && !loadingRater && (
						<PrimaryButton
							text="Select Plans"
							onClick={() => {
								setShowPlanSelector(true)
							}}
						/>
					)}
					{!isPayloadCensusValid && <MessageBar messageBarType={MessageBarType.warning}>You must have at least 5 employees to generate a quote</MessageBar>}
					{loadingQuote && <ProgressIndicator label={`Generating Quote. ${generatingQuoteMessage}`} />}
					{errorGettingQuote && <MessageBar messageBarType={MessageBarType.error}>There was an error running the quote</MessageBar>}
				</Stack>
			)}
			{employees?.length < 5 && (
				<div>
					<span>Group has {employees.length} employees. The group must have at least 5 employees to generate a quote.</span>
				</div>
			)}
			<DialogFooter>
				{quote && !loadingQuote && <PrimaryButton styles={{ root: { background: "green" } }} text="Download Quote" iconProps={{ iconName: "Download" }} onClick={downloadQuote} />}
				{loadingQuote && (
					<DefaultButton
						onClick={() => {
							setQuote(undefined)
							abortQuote()
						}}
						text="Cancel Quote"
					/>
				)}
				<PrimaryButton
					onClick={() => {
						setQuote(undefined)
						setGeneratingQuoteMessage("Please be patient as this can take up to a minute...")
						generateQuote()
						setTimeout(() => {
							setGeneratingQuoteMessage("Compling quote, about 30 more seconds...")
						}, 20000)
						setTimeout(() => {
							setGeneratingQuoteMessage("Almost there....")
						}, 35000)
					}}
					text="Generate Quote"
					disabled={isSubmitDisabled() || !isPayloadCensusValid}
				/>
				{/* {IS_ADMIN && (
					<PrimaryButton
						onClick={async () => {
							setLoadingRater(true)
							try {
								await makeApiCall("runRaterForOpportunity", "POST", JSON.stringify(raterPayload))
								alert("Rater is running in the background. It will be available in the '02-Rater' folder in ~2 minutes.")
							} catch (error) {
								console.error(error)
							} finally {
								setLoadingRater(false)
							}
						}}
						text="Run Rater"
						iconProps={{ iconName: "CloudUpload" }}
						disabled={isRunRaterDisabled() || !isPayloadCensusValid || loadingRater}
					/>
				)} */}
				<DefaultButton onClick={onDismiss} text="Close" />
			</DialogFooter>
			{showPlanSelector && (
				<PlanSelector
					displayOnly={false}
					onDismiss={() => setShowPlanSelector(false)}
					onSave={plans => {
						setSlectedPlans(plans.map(p => p.name))
						setShowPlanSelector(false)
					}}
					defaultSelectedPlans={selectedPlans || []}
					isCRM={props.isCRM}
				/>
			)}
		</div>
	)
}
